/**
 * Utility to access and initialize Application Insights client
 * Supports common initialization patterns and auto-initialization
 */

// Type definitions for Application Insights
interface AppInsights {
  trackTrace: (trace: {
    message: string;
    severityLevel?: number;
    properties?: { [key: string]: string };
  }) => void;
  flush?: () => void;
  context?: {
    user?: {
      authenticatedId?: string;
      id?: string;
    };
    operation?: {
      name?: string;
      id?: string;
    };
  };
  setAuthenticatedUserContext?: (authenticatedUserId: string, accountId?: string) => void;
  clearAuthenticatedUserContext?: () => void;
}

type SeverityLevel = 0 | 1 | 2 | 3 | 4;

const SeverityLevels = {
  Verbose: 0 as SeverityLevel,
  Information: 1 as SeverityLevel,
  Warning: 2 as SeverityLevel,
  Error: 3 as SeverityLevel,
  Critical: 4 as SeverityLevel,
};

let appInsightsInstance: AppInsights | null = null;
let initializationAttempted = false;
let initializationPromise: Promise<AppInsights | null> | null = null;

/**
 * Initialize Application Insights if connection string is available
 */
async function initializeAppInsights(): Promise<AppInsights | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  if (initializationAttempted && appInsightsInstance) {
    return appInsightsInstance;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationAttempted = true;
  const win = window as any;

  // Check if Application Insights is explicitly disabled
  const isEnabled = process.env.REACT_APP_APPLICATIONINSIGHTS_ENABLED !== 'false';
  
  if (!isEnabled) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[AppInsights] Application Insights is disabled via REACT_APP_APPLICATIONINSIGHTS_ENABLED=false');
    }
    return null;
  }

  // Check for connection string in environment variable
  const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;

  // Pattern 1: Check if already initialized on window
  if (win.appInsights && typeof win.appInsights.trackTrace === 'function') {
    appInsightsInstance = win.appInsights;
    if (process.env.NODE_ENV === 'development') {
      console.log('[AppInsights] Using existing window.appInsights instance');
    }
    return appInsightsInstance;
  }

  // Pattern 2: Try to initialize using @microsoft/applicationinsights-web
  if (connectionString) {
    initializationPromise = (async () => {
      try {
        const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
        const appInsights = new ApplicationInsights({
          config: {
            connectionString: connectionString,
            enableAutoRouteTracking: false, // Disable to avoid CORS issues with routing
            enableCorsCorrelation: false, // Disable CORS correlation to prevent API interference
            enableRequestHeaderTracking: false, // Disable to avoid CORS issues
            enableResponseHeaderTracking: false, // Disable to avoid CORS issues
            enableAjaxErrorStatusText: false, // Disable automatic AJAX tracking
            enableAjaxPerfTracking: false, // Disable automatic AJAX performance tracking
            maxAjaxCallsPerView: -1,
            disableAjaxTracking: true, // Disable automatic AJAX/Fetch tracking to prevent CORS issues
            disableFetchTracking: true, // Disable automatic Fetch tracking to prevent CORS issues
            disableExceptionTracking: false, // Keep exception tracking enabled
            disableTelemetry: false,
            // Only track our custom events, not automatic API calls
          },
        });
        appInsights.loadAppInsights();
        appInsightsInstance = appInsights;
        // Also set on window for compatibility
        win.appInsights = appInsights;
        if (process.env.NODE_ENV === 'development') {
          console.log('[AppInsights] Initialized successfully with connection string');
        }
        return appInsightsInstance;
      } catch (error) {
        console.error('[AppInsights] Failed to initialize:', error);
        return null;
      }
    })();
    return initializationPromise;
  }

  // Pattern 3: window.microsoft?.applicationInsights
  if (win.microsoft?.applicationInsights?.ApplicationInsights) {
    try {
      const appInsights = new win.microsoft.applicationInsights.ApplicationInsights({
        config: {
          instrumentationKey: win.microsoft.applicationInsights.instrumentationKey,
        },
      });
      appInsights.loadAppInsights();
      appInsightsInstance = appInsights;
      win.appInsights = appInsights;
      return appInsightsInstance;
    } catch (error) {
      console.error('[AppInsights] Failed to initialize via microsoft.applicationInsights:', error);
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.warn('[AppInsights] No connection string found and no existing instance. Set REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING environment variable.');
  }

  return null;
}

/**
 * Initialize Application Insights (call this early in app lifecycle)
 */
export async function initApplicationInsights(): Promise<void> {
  await initializeAppInsights();
}

/**
 * Get the Application Insights client from the global scope
 * Returns null if not available
 * Note: This will attempt initialization if not already done
 */
export function getAppInsightsClient(): AppInsights | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // Return cached instance if available
  if (appInsightsInstance) {
    return appInsightsInstance;
  }

  const win = window as any;

  // Try existing patterns first
  if (win.appInsights && typeof win.appInsights.trackTrace === 'function') {
    appInsightsInstance = win.appInsights;
    return appInsightsInstance;
  }

  // Try to initialize synchronously (will work if already initialized)
  // For async initialization, call initApplicationInsights() early
  if (!initializationAttempted) {
    // Trigger async initialization but return null for now
    initializeAppInsights().catch((error) => {
      console.error('[AppInsights] Initialization error:', error);
    });
  }

  return appInsightsInstance;
}

/**
 * Set authenticated user context for Application Insights
 * This binds all telemetry to the logged-in user
 * @param userId - User's email or unique identifier
 * @param accountId - Optional account identifier
 */
export function setAuthenticatedUser(userId: string, accountId?: string): void {
  const client = getAppInsightsClient();
  
  if (!client) {
    return;
  }

  try {
    // Set authenticated user context if available
    if (client.setAuthenticatedUserContext && typeof client.setAuthenticatedUserContext === 'function') {
      client.setAuthenticatedUserContext(userId, accountId);
    } else if (client.context?.user) {
      // Fallback: set user context directly
      client.context.user.authenticatedId = userId;
      if (accountId) {
        client.context.user.id = accountId;
      }
    }
  } catch (error) {
    console.error('[AppInsights] Error setting authenticated user:', error);
  }
}

/**
 * Clear authenticated user context
 */
export function clearAuthenticatedUser(): void {
  const client = getAppInsightsClient();
  
  if (!client) {
    return;
  }

  try {
    if (client.clearAuthenticatedUserContext && typeof client.clearAuthenticatedUserContext === 'function') {
      client.clearAuthenticatedUserContext();
    } else if (client.context?.user) {
      client.context.user.authenticatedId = undefined;
      client.context.user.id = undefined;
    }
  } catch (error) {
    console.error('[AppInsights] Error clearing authenticated user:', error);
  }
}

/**
 * Track a trace log to Application Insights
 * @param message - The trace message
 * @param properties - Additional properties to include
 * @param severity - Severity level (default: Information)
 * @param operationName - Operation name for correlation (optional)
 */
export function trackTrace(
  message: string,
  properties?: { [key: string]: string },
  severity: SeverityLevel = SeverityLevels.Information,
  operationName?: string
): void {
  const client = getAppInsightsClient();
  
  if (!client) {
    // Always log in development to help debug
    if (process.env.NODE_ENV === 'development') {
      console.warn('[AppInsights] Client not available. Trace not sent:', message, properties);
      console.warn('[AppInsights] Available on window:', {
        appInsights: typeof (window as any).appInsights,
        microsoft: typeof (window as any).Microsoft?.ApplicationInsights,
        connectionString: process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING ? 'SET' : 'NOT SET',
      });
    }
    return;
  }

  try {
    // Set operation name if provided
    if (operationName && client.context?.operation) {
      client.context.operation.name = operationName;
    }

    // Ensure properties are properly formatted
    const traceProperties: { [key: string]: string } = {};
    if (properties) {
      Object.entries(properties).forEach(([key, value]) => {
        traceProperties[key] = String(value);
      });
    }

    // Add operation name to properties for easier querying
    if (operationName) {
      traceProperties.operationName = operationName;
    }

    client.trackTrace({
      message,
      severityLevel: severity,
      properties: traceProperties,
    });

    // Log in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('[AppInsights] Trace sent:', message, { ...traceProperties, operationName });
    }

    // Flush if available to ensure immediate sending
    if (client.flush && typeof client.flush === 'function') {
      client.flush();
    }
  } catch (error) {
    console.error('[AppInsights] Error tracking trace:', error);
  }
}

/**
 * Track dashboard load event
 */
export function trackDashboardLoad(
  pageName: string,
  userName: string,
  appliedFilters: { [key: string]: string | number | boolean | string[] } = {},
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
  }
): void {
  const properties: { [key: string]: string } = {
    eventName: 'Dashboard',
    pageName,
    timestamp: new Date().toISOString(),
  };

  // Map login identity: userEmail is the raw identifier, userName is the full name when available
  const userEmail = userName;
  let fullName = '';

  if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
    const first = (userRoleInfo.firstName || '').trim();
    const last = (userRoleInfo.lastName || '').trim();
    fullName = `${first} ${last}`.trim();
  }

  properties.userEmail = userEmail;
  properties.userName = fullName || userEmail;

  // Add user role and permission information
  if (userRoleInfo) {
    if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
    if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
    if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
    if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
    if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);
    
    // Add customerId and customerName as top-level properties
    if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
    if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;
  }

  // Build operations object with filter mappings
  const operations: { [key: string]: any } = {
    action: 'Dashboard Loaded',
  };

  // Process filters and apply field mappings
  Object.entries(appliedFilters).forEach(([key, value]) => {
    // Only include filters that have meaningful values
    if (Array.isArray(value)) {
      if (value.length > 0) {
        // Map field names: assignedGroupName/assignmentGroupName → assignmentGroupIds
        if (key === 'assignedGroupName' || key === 'assignmentGroupName' || key === 'assignmentGroupId') {
          // Ensure it's stored as assignmentGroupIds (plural, array)
          // If assignmentGroupIds already exists, merge arrays
          if (operations.assignmentGroupIds && Array.isArray(operations.assignmentGroupIds)) {
            operations.assignmentGroupIds = [...operations.assignmentGroupIds, ...value];
          } else {
            operations.assignmentGroupIds = value;
          }
        } else if (key === 'customerName') {
          // Map customerName → customerId (only if customerId doesn't already exist)
          if (!operations.customerId) {
            operations.customerId = value.length === 1 ? value[0] : value;
          }
        } else if (key === 'customerId') {
          // Keep customerId as is
          operations.customerId = value.length === 1 ? value[0] : value;
        } else if (key === 'assignmentGroupIds') {
          // Keep assignmentGroupIds as is
          operations.assignmentGroupIds = value;
        } else {
          operations[key] = value;
        }
      }
    } else if (typeof value === 'string' && value.trim().length > 0) {
      // Map field names: assignedGroupName/assignmentGroupName → assignmentGroupIds (as array)
      if (key === 'assignedGroupName' || key === 'assignmentGroupName' || key === 'assignmentGroupId') {
        // If assignmentGroupIds already exists, add to array
        if (operations.assignmentGroupIds && Array.isArray(operations.assignmentGroupIds)) {
          operations.assignmentGroupIds.push(value);
        } else {
          operations.assignmentGroupIds = [value];
        }
      } else if (key === 'customerName') {
        // Map customerName → customerId (only if customerId doesn't already exist)
        if (!operations.customerId) {
          operations.customerId = value;
        }
      } else if (key === 'customerId') {
        // Keep customerId as is
        operations.customerId = value;
      } else if (key === 'assignmentGroupIds') {
        // This shouldn't happen (should be array), but handle it
        operations.assignmentGroupIds = [value];
      } else {
        operations[key] = value;
      }
    } else if (typeof value === 'number' && !isNaN(value)) {
      // Skip customerName even if it's a number (shouldn't happen, but safety check)
      if (key !== 'customerName') {
        operations[key] = value;
      }
    } else if (typeof value === 'boolean') {
      // Skip customerName even if it's a boolean (shouldn't happen, but safety check)
      if (key !== 'customerName') {
        operations[key] = value;
      }
    }
  });

  // Convert operations to JSON string
  properties.operations = JSON.stringify(operations);

  // Add operationName for correlation
  const operationName = 'Dashboard_Load';
  properties.operationName = operationName;

  trackTrace('Dashboard', properties, SeverityLevels.Information, 'Dashboard_Load');
}

/**
 * Track dashboard filter change event
 */
export function trackDashboardFilterChange(
  pageName: string,
  userName: string,
  appliedFilters: { [key: string]: string | number | boolean | string[] } = {},
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
  }
): void {
  const properties: { [key: string]: string } = {
    eventName: 'Dashboard',
    pageName,
    timestamp: new Date().toISOString(),
  };

  // Map login identity: userEmail is the raw identifier, userName is the full name when available
  const userEmail = userName;
  let fullName = '';

  if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
    const first = (userRoleInfo.firstName || '').trim();
    const last = (userRoleInfo.lastName || '').trim();
    fullName = `${first} ${last}`.trim();
  }

  properties.userEmail = userEmail;
  properties.userName = fullName || userEmail;

  // Add user role and permission information
  if (userRoleInfo) {
    if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
    if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
    if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
    if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
    if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);
    
    // Add customerId and customerName as top-level properties
    if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
    if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;
  }

  // Build operations object with filter mappings
  const operations: { [key: string]: any } = {
    action: 'Filter Applied',
  };

  // Process filters and apply field mappings
  Object.entries(appliedFilters).forEach(([key, value]) => {
    // Only include filters that have meaningful values
    if (Array.isArray(value)) {
      if (value.length > 0) {
        // Map field names: assignedGroupName/assignmentGroupName → assignmentGroupIds
        if (key === 'assignedGroupName' || key === 'assignmentGroupName' || key === 'assignmentGroupId') {
          // Ensure it's stored as assignmentGroupIds (plural, array)
          // If assignmentGroupIds already exists, merge arrays
          if (operations.assignmentGroupIds && Array.isArray(operations.assignmentGroupIds)) {
            operations.assignmentGroupIds = [...operations.assignmentGroupIds, ...value];
          } else {
            operations.assignmentGroupIds = value;
          }
        } else if (key === 'customerName') {
          // Map customerName → customerId (only if customerId doesn't already exist)
          if (!operations.customerId) {
            operations.customerId = value.length === 1 ? value[0] : value;
          }
        } else if (key === 'customerId') {
          // Keep customerId as is
          operations.customerId = value.length === 1 ? value[0] : value;
        } else if (key === 'assignmentGroupIds') {
          // Keep assignmentGroupIds as is
          operations.assignmentGroupIds = value;
        } else if (key === 'customerName') {
          // Skip customerName - only customerId should be in operations
        } else {
          operations[key] = value;
        }
      }
    } else if (typeof value === 'string' && value.trim().length > 0) {
      // Map field names: assignedGroupName/assignmentGroupName → assignmentGroupIds (as array)
      if (key === 'assignedGroupName' || key === 'assignmentGroupName' || key === 'assignmentGroupId') {
        // If assignmentGroupIds already exists, add to array
        if (operations.assignmentGroupIds && Array.isArray(operations.assignmentGroupIds)) {
          operations.assignmentGroupIds.push(value);
        } else {
          operations.assignmentGroupIds = [value];
        }
      } else if (key === 'customerName') {
        // Map customerName → customerId (only if customerId doesn't already exist)
        if (!operations.customerId) {
          operations.customerId = value;
        }
        // Do not add customerName to operations - only customerId
      } else if (key === 'customerId') {
        // Keep customerId as is
        operations.customerId = value;
      } else if (key === 'assignmentGroupIds') {
        // This shouldn't happen (should be array), but handle it
        operations.assignmentGroupIds = [value];
      } else {
        operations[key] = value;
      }
    } else if (typeof value === 'number' && !isNaN(value)) {
      // Skip customerName even if it's a number (shouldn't happen, but safety check)
      if (key !== 'customerName') {
        operations[key] = value;
      }
    } else if (typeof value === 'boolean') {
      // Skip customerName even if it's a boolean (shouldn't happen, but safety check)
      if (key !== 'customerName') {
        operations[key] = value;
      }
    }
  });

  // Convert operations to JSON string
  properties.operations = JSON.stringify(operations);

  // Add operationName for correlation
  const operationName = 'Dashboard_Filter_Change';
  properties.operationName = operationName;

  trackTrace('Dashboard', properties, SeverityLevels.Information, 'Dashboard_Filter_Change');
}

/**
 * Track Incident module operation
 * @param pageName - "Incident List" | "Incident Details" | "Incident Created" | "Problem Created on Incident"
 * @param userName - User's email or name
 * @param operations - Object containing operation details (will be stringified as JSON)
 * @param userRoleInfo - Optional user role and permission information
 */
export function trackIncidentOperation(
  pageName: 'Incident List' | 'Incident Details' | 'Incident Details - Edit' | 'Incident Created' | 'Problem Created on Incident',
  userName: string,
  operations: { [key: string]: string | number | boolean | null | undefined },
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
  }
): void {
  try {
    const properties: { [key: string]: string } = {
      eventName: 'Incident Module',
      pageName,
      timestamp: new Date().toISOString(),
    };

    // Map login identity: userEmail is the raw identifier, userName is the full name when available
    const userEmail = userName;
    let fullName = '';

    if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
      const first = (userRoleInfo.firstName || '').trim();
      const last = (userRoleInfo.lastName || '').trim();
      fullName = `${first} ${last}`.trim();
    }

    properties.userEmail = userEmail;
    properties.userName = fullName || userEmail;

    // Add user role and permission information
    if (userRoleInfo) {
      if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
      if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
      if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
      if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
      if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);

      // Add customerId and customerName as top-level properties
      if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
      if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;
    }

    // Convert operations object to JSON string, filtering out null/undefined values
    const cleanOperations: { [key: string]: string | number | boolean } = {};
    Object.entries(operations).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleanOperations[key] = value;
      }
    });

    properties.operations = JSON.stringify(cleanOperations);

    // Extract operationName from operations.action if available
    if (cleanOperations.action) {
      properties.operationName = String(cleanOperations.action);
    }

    // Remove operationName if it is the same as eventName
    if (properties.operationName && properties.operationName === properties.eventName) {
      delete properties.operationName;
    }

    trackTrace('Incident Module', properties, SeverityLevels.Information, 'Incident Module');
  } catch (error) {
    console.error('Failed to track incident operation:', error);
  }
}

/**
 * Track Change module operation
 * @param pageName - "Changes List" | "Change Details" | "Change Details - Edit" | "Change Created" | "Change Details - Service Request Created"
 * @param userName - User's email or name
 * @param operations - Object containing operation details (will be stringified as JSON)
 * @param userRoleInfo - Optional user role and permission information
 */
export function trackChangeOperation(
  pageName: 'Changes List' | 'Change Details' | 'Change Details - Edit' | 'Change Created' | 'Change Details - Change Created' | 'Change Details - Service Request Created',
  userName: string,
  operations: { [key: string]: string | number | boolean | null | undefined },
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
  }
): void {
  try {
    const properties: { [key: string]: string } = {
      eventName: 'Changes Module',
      pageName,
      timestamp: new Date().toISOString(),
    };

    // Map login identity: userEmail is the raw identifier, userName is the full name when available
    const userEmail = userName;
    let fullName = '';

    if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
      const first = (userRoleInfo.firstName || '').trim();
      const last = (userRoleInfo.lastName || '').trim();
      fullName = `${first} ${last}`.trim();
    }

    properties.userEmail = userEmail;
    properties.userName = fullName || userEmail;

    // Add user role and permission information
    if (userRoleInfo) {
      if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
      if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
      if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
      if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
      if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);

      // Add customerId and customerName as top-level properties
      if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
      if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;
    }

    // Convert operations object to JSON string, filtering out null/undefined values
    const cleanOperations: { [key: string]: string | number | boolean } = {};
    Object.entries(operations).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleanOperations[key] = value;
      }
    });

    properties.operations = JSON.stringify(cleanOperations);

    // Extract operationName from operations.action if available
    if (cleanOperations.action) {
      properties.operationName = String(cleanOperations.action);
    }

    // Remove operationName if it is the same as eventName
    if (properties.operationName && properties.operationName === properties.eventName) {
      delete properties.operationName;
    }

    trackTrace('Changes Module', properties, SeverityLevels.Information, 'Changes Module');
  } catch (error) {
    console.error('Failed to track change operation:', error);
  }
}

/**
 * Track Service Request module operation
 * @param pageName - "Service Requests List" | "Service Request Details" | "Service Request Details - Edit" | "Service Request Created"
 * @param userName - User's email or name
 * @param operations - Object containing operation details (will be stringified as JSON)
 * @param userRoleInfo - Optional user role and permission information
 */
export function trackServiceRequestOperation(
  pageName: 'Service Requests List' | 'Service Request Details' | 'Service Request Details - Edit' | 'Service Request Created',
  userName: string,
  operations: { [key: string]: string | number | boolean | null | undefined },
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
  }
): void {
  try {
    const properties: { [key: string]: string } = {
      eventName: 'Service Request Module',
      pageName,
      timestamp: new Date().toISOString(),
    };

    // Map login identity: userEmail is the raw identifier, userName is the full name when available
    const userEmail = userName;
    let fullName = '';

    if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
      const first = (userRoleInfo.firstName || '').trim();
      const last = (userRoleInfo.lastName || '').trim();
      fullName = `${first} ${last}`.trim();
    }

    properties.userEmail = userEmail;
    properties.userName = fullName || userEmail;

    // Add user role and permission information
    if (userRoleInfo) {
      if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
      if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
      if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
      if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
      if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);
      
      // Add customerId and customerName as top-level properties
      if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
      if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;

    }

    // Convert operations object to JSON string, filtering out null/undefined values
    const cleanOperations: { [key: string]: string | number | boolean } = {};
    Object.entries(operations).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleanOperations[key] = value;
      }
    });

    properties.operations = JSON.stringify(cleanOperations);

    // Extract operationName from operations.action if available
    if (cleanOperations.action) {
      properties.operationName = String(cleanOperations.action);
    }

    // Remove operationName if it is the same as eventName
    if (properties.operationName && properties.operationName === properties.eventName) {
      delete properties.operationName;
    }

    trackTrace('Service Request Module', properties, SeverityLevels.Information, 'Service Request Module');
  } catch (error) {
    console.error('Failed to track service request operation:', error);
  }
}

/**
 * Track Alert module operation
 * @param pageName - "Alert List" | "Alert Details"
 * @param userName - User's email or name
 * @param operations - Object containing operation details (will be stringified as JSON)
 * @param userRoleInfo - Optional user role and permission information
 */
export function trackAlertOperation(
  pageName: 'Alert List' | 'Alert Details',
  userName: string,
  operations: { [key: string]: string | number | boolean | null | undefined },
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
  }
): void {
  try {
    const properties: { [key: string]: string } = {
      eventName: 'Alert Module',
      pageName,
      timestamp: new Date().toISOString(),
    };

    // Map login identity: userEmail is the raw identifier, userName is the full name when available
    const userEmail = userName;
    let fullName = '';

    if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
      const first = (userRoleInfo.firstName || '').trim();
      const last = (userRoleInfo.lastName || '').trim();
      fullName = `${first} ${last}`.trim();
    }

    properties.userEmail = userEmail;
    properties.userName = fullName || userEmail;

    // Add user role and permission information
    if (userRoleInfo) {
      if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
      if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
      if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
      if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
      if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);
      
      // Add customerId and customerName as top-level properties
      if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
      if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;

    }

    // Convert operations object to JSON string, filtering out null/undefined values
    const cleanOperations: { [key: string]: string | number | boolean } = {};
    Object.entries(operations).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleanOperations[key] = value;
      }
    });

    properties.operations = JSON.stringify(cleanOperations);

    // Extract operationName from operations.action if available
    if (cleanOperations.action) {
      properties.operationName = String(cleanOperations.action);
    }

    // Remove operationName if it is the same as eventName
    if (properties.operationName && properties.operationName === properties.eventName) {
      delete properties.operationName;
    }

    trackTrace('Alert Module', properties, SeverityLevels.Information, 'Alert Module');
  } catch (error) {
    console.error('Failed to track alert operation:', error);
  }
}

/**
 * Track Problem module operation
 * Independent implementation for Problem Module logging
 * @param pageName - "Problems List" | "Problem Details" | "Problem Details - Edit" | "Problem Created" | "Problem Details - Service Request Created" | "Problem Details - Change Created"
 * @param userName - User's email or name
 * @param operations - Object containing operation details (will be stringified as JSON)
 * @param userRoleInfo - Optional user role and permission information
 */
export function trackProblemOperation(
  pageName: 'Problems List' | 'Problem Details' | 'Problem Details - Edit' | 'Problem Created' | 'Problem Details - Service Request Created' | 'Problem Details - Change Created',
  userName: string,
  operations: { [key: string]: string | number | boolean | null | undefined },
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
  }
): void {
  try {
    const properties: { [key: string]: string } = {
      eventName: 'Problem Module',
      pageName,
      timestamp: new Date().toISOString(),
    };

    // Map login identity: userEmail is the raw identifier, userName is the full name when available
    const userEmail = userName;
    let fullName = '';

    if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
      const first = (userRoleInfo.firstName || '').trim();
      const last = (userRoleInfo.lastName || '').trim();
      fullName = `${first} ${last}`.trim();
    }

    properties.userEmail = userEmail;
    properties.userName = fullName || userEmail;

    // Add user role and permission information
    if (userRoleInfo) {
      if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
      if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
      if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
      if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
      if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);
      
      // Add customerId and customerName as top-level properties
      if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
      if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;

    }

    // Convert operations object to JSON string, filtering out null/undefined values
    const cleanOperations: { [key: string]: string | number | boolean } = {};
    Object.entries(operations).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleanOperations[key] = value;
      }
    });

    properties.operations = JSON.stringify(cleanOperations);

    // Extract operationName from operations.action if available
    if (cleanOperations.action) {
      properties.operationName = String(cleanOperations.action);
    }

    // Remove operationName if it is the same as eventName
    if (properties.operationName && properties.operationName === properties.eventName) {
      delete properties.operationName;
    }

    trackTrace('Problem Module', properties, SeverityLevels.Information, 'Problem Module');
  } catch (error) {
    console.error('Failed to track problem operation:', error);
  }
}

/**
 * Track Case module operation
 * Independent implementation for Case Module logging
 * @param pageName - "Cases List" | "Case Details" | "Case Details - Edit" | "Case Created" | "Case Details - Service Request Converted" | "Case Details - Change Converted" | "Case Details - Problem Converted" | "Case Details - Incident Converted"
 * @param userName - User's email or name
 * @param operations - Object containing operation details (will be stringified as JSON)
 * @param userRoleInfo - Optional user role and permission information
 */
export function trackCaseOperation(
  pageName: 'Cases List' | 'Case Details' | 'Case Details - Edit' | 'Case Created' | 'Case Details - Service Request Converted' | 'Case Details - Change Converted' | 'Case Details - Problem Converted' | 'Case Details - Incident Converted',
  userName: string,
  operations: { [key: string]: string | number | boolean | null | undefined },
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
  }
): void {
  try {
    const properties: { [key: string]: string } = {
      eventName: 'Case Module',
      pageName,
      timestamp: new Date().toISOString(),
    };

    // Map login identity: userEmail is the raw identifier, userName is the full name when available
    const userEmail = userName;
    let fullName = '';

    if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
      const first = (userRoleInfo.firstName || '').trim();
      const last = (userRoleInfo.lastName || '').trim();
      fullName = `${first} ${last}`.trim();
    }

    properties.userEmail = userEmail;
    properties.userName = fullName || userEmail;

    // Add user role and permission information
    if (userRoleInfo) {
      if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
      if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
      if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
      if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
      if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);
      
      // Add customerId and customerName as top-level properties
      if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
      if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;

    }

    // Convert operations object to JSON string, filtering out null/undefined values
    const cleanOperations: { [key: string]: string | number | boolean } = {};
    Object.entries(operations).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleanOperations[key] = value;
      }
    });

    properties.operations = JSON.stringify(cleanOperations);

    // Extract operationName from operations.action if available
    if (cleanOperations.action) {
      properties.operationName = String(cleanOperations.action);
    }

    // Remove operationName if it is the same as eventName
    if (properties.operationName && properties.operationName === properties.eventName) {
      delete properties.operationName;
    }

    trackTrace('Case Module', properties, SeverityLevels.Information, 'Case Module');
  } catch (error) {
    console.error('Failed to track case operation:', error);
  }
}

/**
 * Format milliseconds to human-readable time string (e.g., "2 hours 15 minutes")
 */
function formatActiveTime(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  const parts: string[] = [];
  
  // Add hours only if greater than 0
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  
  // Add minutes only if greater than 0
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  
  // Add seconds only if hours and minutes are both 0, or if seconds > 0
  if (hours === 0 && minutes === 0) {
    // If less than 1 minute, show only seconds
    return `${seconds}s`;
  } else if (seconds > 0) {
    // If there are hours or minutes, also show seconds if > 0
    parts.push(`${seconds}s`);
  }
  
  return parts.join(' ');
}

/**
 * Track user session activity (Login/Logout)
 * Independent implementation - does not use trackIncidentOperation
 * Uses the same log schema format as Incident logs for consistency
 * 
 * @param action - "User Login" | "User Logout"
 * @param userName - User's email or name
 * @param operations - Object containing operation details (will be stringified as JSON)
 * @param userRoleInfo - User role and permission information
 */
export function trackUserSessionLog(
  action: 'User Login' | 'User Logout',
  userName: string,
  operations: { [key: string]: string | number | boolean | null | undefined },
  userRoleInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
    roleType?: string;
    roleLevel?: number;
    userClass?: string;
    customerId?: string;
    customerName?: string;
    engagementId?: string;
    engagementName?: string;
    isServiceDeskUser?: boolean;
    sessionId?: string;
    activeTime?: number; // Active time in milliseconds (for logout)
  }
): void {
  const properties: { [key: string]: string } = {
    eventName: 'User Login Session',
    pageName: action,
    timestamp: new Date().toISOString(),
  };

  // Map login identity: userEmail is the raw identifier, userName is the full name when available
  const userEmail = userName;
  let fullName = '';

  if (userRoleInfo && (userRoleInfo.firstName || userRoleInfo.lastName)) {
    const first = (userRoleInfo.firstName || '').trim();
    const last = (userRoleInfo.lastName || '').trim();
    fullName = `${first} ${last}`.trim();
  }

  properties.userEmail = userEmail;
  properties.userName = fullName || userEmail;

  // Add user role and permission information
  if (userRoleInfo) {
    if (userRoleInfo.role) properties.userRole = userRoleInfo.role;
    if (userRoleInfo.roleType) properties.userRoleType = userRoleInfo.roleType;
    if (userRoleInfo.roleLevel !== undefined) properties.userRoleLevel = String(userRoleInfo.roleLevel);
    if (userRoleInfo.userClass) properties.userClass = userRoleInfo.userClass;
    if (userRoleInfo.isServiceDeskUser !== undefined) properties.isServiceDeskUser = String(userRoleInfo.isServiceDeskUser);
    
    // Add customerId and customerName as top-level properties
    if (userRoleInfo.customerId) properties.customerId = userRoleInfo.customerId;
    if (userRoleInfo.customerName) properties.customerName = userRoleInfo.customerName;

    // Add session ID if provided
    if (userRoleInfo.sessionId) {
      properties.sessionId = userRoleInfo.sessionId;
    }
  }

  // Convert operations object to JSON string, filtering out null/undefined values
  const cleanOperations: { [key: string]: string | number | boolean } = {};
  Object.entries(operations).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      cleanOperations[key] = value;
    }
  });

  // Add loginMethod to customDimensions (properties) if present in operations
  if (cleanOperations.loginMethod && typeof cleanOperations.loginMethod === 'string') {
    properties.loginMethod = cleanOperations.loginMethod;
  }

  // Add session ID to operations if provided (for both login and logout)
  if (userRoleInfo?.sessionId) {
    cleanOperations.sessionId = userRoleInfo.sessionId;
  }

  // Add active time to operations if provided (for logout)
  if (userRoleInfo?.activeTime !== undefined && userRoleInfo.activeTime > 0) {
    const formattedActiveTime = formatActiveTime(userRoleInfo.activeTime);
    cleanOperations.activeTime = formattedActiveTime;
    // Also add activeTime to customDimensions (properties) for easier KQL querying
    properties.activeTime = formattedActiveTime;
  }

  properties.operations = JSON.stringify(cleanOperations);

  // Set operation name for correlation
  const operationName = action === 'User Login' ? 'User_Login' : 'User_Logout';
  properties.operationName = operationName;

  trackTrace(action, properties, SeverityLevels.Information, operationName);
}

/**
 * Test function to verify Application Insights is working
 * Call this from browser console: window.testAppInsights()
 */
export function testAppInsights(): void {
  const client = getAppInsightsClient();
  if (client) {
    trackTrace('Test trace from testAppInsights()', {
      test: 'true',
      timestamp: new Date().toISOString(),
    }, SeverityLevels.Information);
    console.log('✅ Application Insights test trace sent. Check Azure Portal in 1-2 minutes.');
    console.log('Client:', client);
  } else {
    console.error('❌ Application Insights client not available');
    console.log('Connection String:', process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING ? 'SET' : 'NOT SET');
    console.log('Window.appInsights:', (window as any).appInsights);
  }
}

// Expose test function to window for easy debugging
if (typeof window !== 'undefined') {
  (window as any).testAppInsights = testAppInsights;
}

